import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './pages/Dashboard';
import CreateUser from './pages/CreateUser';
import LeadForm from './pages/forms/LeadForm';
import WpForm from './pages/forms/WpForm';
import PvAndWPForm from './pages/forms/PvAndWPForm';
import AddPvLeadToCustomer from './pages/forms/AddPvLeadToCustomer';
import AddWpLeadToCustomer from './pages/forms/AddWpLeadToCustomer';
import Stellungnahmen from './pages/Stellungnahmen';
import Termine from './pages/Termine';
import TerminListe from './pages/TerminListe';
import Leads from './pages/Leads';
import LeadsOverview from './pages/LeadsOverview';

import TeamDashboard from './components/dashboard/TeamDashboard';
import OrganizationDashboard from './components/dashboard/OrganizationDashboard';
import OrganizationManagement from './pages/organization/OrganizationManagement';
import OrganizationMembersManagement from './pages/organization/OrganizationMembersManagement';
import TeamLeads from './pages/organization/TeamLeads';
import OrganizationLeads from './pages/organization/OrganizationLeads';
import CustomerDetailPage from './pages/CustomerDetailPage';
import GlobalSubTeamManagement from './components/common/GlobalSubTeamManagement';
import MyBewertungen from './pages/MyBewertungen';
import AgenturenVerwalten from './components/dashboard/AgenturenVerwalten';
import AgenturManagementWrapper from './components/dashboard/AgenturManagementWrapper';
import AgenturDashboard from './components/dashboard/AgenturDashboard';
import AgenturManagement from './components/dashboard/AgenturManagement';
import AgenturLeads from './pages/Agenturen/AgenturLeads';
import AgenturTeams from './pages/AgenturTeams';
import TeamMitarbeiter from './pages/TeamMitarbeiter';

// Import der Navigation
import DashboardNav from './components/dashboard/DashboardNav';

import CallcenterLeads from './pages/CallcenterLeads';
import CallcenterDashboard from './components/dashboard/CallcenterDashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import OptInConfirmation from './pages/OptInConfirmation';
import WarehouseManagement from './pages/WarehouseManagement';
import Orders from './pages/Orders';
import OrderManagement from './pages/OrderManagement';

// Tab-Titel Utility
import { updateTabTitle, resetTabTitle } from './utils/tabTitle';

import { checkIsSubTeamleiter } from './api';

// Session Management
import { useSessionTimeout, useExtendedSessionMonitoring, initializeSession, cleanupSession } from './utils/sessionTimeout';

function AgenturDashboardRouteWrapper({ user }) {
  const { id } = useParams();
  return <AgenturDashboard user={user} selectedAgentur={{ id }} />;
}

function AgenturManagementRouteWrapper({ user }) {
  const { id } = useParams();
  return <AgenturManagement user={user} agenturId={id} />;
}

function AgenturLeadsRouteWrapper({ user }) {
  const { id } = useParams();
  return <AgenturLeads user={user} agenturId={id} />;
}

// Wrapper für Callcenter Leads mit verschiedenen Filtern
function CallcenterLeadsWrapper({ user, filter }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="callcenter-leads"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <CallcenterLeads user={user} filter={filter} />
      </div>
    </>
  );
}

// Wrapper-Komponente für Stellungnahmen mit Navigation
function StellungnahmenWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleNavigateToCustomer = (customerId) => {
    navigate(`/dashboard?customer=${customerId}`);
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="stellungnahmen"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <Stellungnahmen 
          user={user} 
          onNavigateToCustomer={handleNavigateToCustomer}
        />
      </div>
    </>
  );
}

// Wrapper für normale Leads/Kunden (ohne doppelte Navigation)
function LeadsWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="leads"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <Leads user={user} />
      </div>
    </>
  );
}

// Wrapper für Termine
function TermineWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="termine"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <Termine user={user} />
      </div>
    </>
  );
}

// Wrapper für TerminListe
function TerminListeWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="termin-liste"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <TerminListe user={user} />
      </div>
    </>
  );
}

// Wrapper für Team Dashboard Route - MIT NAVIGATION
function TeamDashboardWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="team-dashboard"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <div className="page-header">
          <button className="back-button" onClick={handleBackToDashboard}>
            ← Zurück zum Dashboard
          </button>
          <h1>Team Dashboard</h1>
        </div>
        <TeamDashboard 
          user={user}
          selectedTeam={user?.team}
          onBackToOrganization={handleBackToDashboard}
        />
      </div>
    </>
  );
}

// Wrapper für Organization Dashboard Route - MIT NAVIGATION
function OrganizationDashboardWrapper({ user }) {
  const navigate = useNavigate();
  const { organizationId } = useParams(); // organizationId aus URL lesen
  
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleNavigateToTeamDashboard = (teamId, teamName) => {
    // organizationId aus der aktuellen URL verwenden
    const currentOrgId = organizationId;
    
    // organizationId als URL-Parameter hinzufügen, falls vorhanden
    const params = new URLSearchParams();
    params.set('name', teamName);
    if (currentOrgId) {
      params.set('orgId', currentOrgId);
    }
    navigate(`/team-dashboard/${teamId}?${params.toString()}`);
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    } else if (section === 'org-dashboard') {
      // Verwende die organizationId aus der URL falls vorhanden
      if (organizationId) {
        navigate(`/org-dashboard/${organizationId}`);
      } else {
        navigate('/org-dashboard');
      }
    } else if (section === 'team-dashboard') {
      navigate('/my-team-dashboard');
    } else if (section === 'organization-management') {
      // Verwende die organizationId aus der URL falls vorhanden
      if (organizationId) {
        navigate(`/organization-management/${organizationId}`);
      } else {
        navigate('/organization-management');
      }
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="org-dashboard"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => {
          if (organizationId) {
            navigate(`/organization-management/${organizationId}`);
          } else {
            navigate('/organization-management');
          }
        }}
        onOpenMembersManagement={() => {
          if (organizationId) {
            navigate(`/organization-members/${organizationId}`);
          } else {
            navigate('/organization-members');
          }
        }}
        onViewOrganizationLeads={() => {
          if (organizationId) {
            navigate(`/organization-leads/${organizationId}`);
          } else {
            navigate('/organization-leads');
          }
        }}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <OrganizationDashboard 
          user={user} 
          organizationId={organizationId} // organizationId an Komponente übergeben
          onNavigateToTeamDashboard={handleNavigateToTeamDashboard}
        />
      </div>
    </>
  );
}

// Wrapper für Organization Management Route - MIT NAVIGATION
function OrganizationManagementWrapper({ user }) {
  const navigate = useNavigate();
  const { organizationId } = useParams(); // organizationId aus URL lesen
  
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleViewTeamLeads = (teamId, teamName) => {
    navigate(`/team-leads/${teamId}?name=${encodeURIComponent(teamName)}`);
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    } else if (section === 'org-dashboard') {
      // Verwende die organizationId aus der URL falls vorhanden
      if (organizationId) {
        navigate(`/org-dashboard/${organizationId}`);
      } else {
        navigate('/org-dashboard');
      }
    } else if (section === 'team-dashboard') {
      navigate('/my-team-dashboard');
    } else if (section === 'organization-management') {
      // Verwende die organizationId aus der URL falls vorhanden
      if (organizationId) {
        navigate(`/organization-management/${organizationId}`);
      } else {
        navigate('/organization-management');
      }
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="organization-management"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => {
          if (organizationId) {
            navigate(`/organization-management/${organizationId}`);
          } else {
            navigate('/organization-management');
          }
        }}
        onOpenMembersManagement={() => {
          if (organizationId) {
            navigate(`/organization-members/${organizationId}`);
          } else {
            navigate('/organization-members');
          }
        }}
        onViewOrganizationLeads={() => {
          if (organizationId) {
            navigate(`/organization-leads/${organizationId}`);
          } else {
            navigate('/organization-leads');
          }
        }}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <OrganizationManagement 
          user={user} 
          organizationId={organizationId} // organizationId an Komponente übergeben
          onBack={handleBackToDashboard}
          onViewTeamLeads={handleViewTeamLeads}
        />
      </div>
    </>
  );
}

// Wrapper für Team Leads Route - für Admin/Orgleiter/Teamleiter
function TeamLeadsWrapper({ user }) {
  const navigate = useNavigate();
  const { teamId, customerId } = useParams(); // Beide Parameter aus URL lesen
  const urlParams = new URLSearchParams(window.location.search);
  const teamName = urlParams.get('name') || 'Team';
  
  const handleBackToOrganization = () => {
    navigate('/organization-management');
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleBackToTeamLeads = () => {
    navigate(`/team-leads/${teamId}?name=${encodeURIComponent(teamName)}`);
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    } else if (section === 'org-dashboard') {
      navigate('/org-dashboard');
    } else if (section === 'team-dashboard') {
      navigate('/my-team-dashboard');
    } else if (section === 'organization-management') {
      navigate('/organization-management');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="team-leads"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <TeamLeads
          user={user}
          teamId={teamId}
          teamName={teamName}
          customerId={customerId}
          onBack={customerId ? handleBackToTeamLeads : (user?.isTeamlead ? handleBackToDashboard : handleBackToOrganization)}
        />
      </div>
    </>
  );
}

// Wrapper für Organisation Leads Route - OHNE doppelte Navigation
function OrganizationLeadsWrapper({ user }) {
  const navigate = useNavigate();
  const { organizationId } = useParams(); // organizationId aus URL lesen

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <OrganizationLeads
      user={user}
      organizationId={organizationId ? parseInt(organizationId) : null} // Explizite Integer-Konvertierung
      organizationName="Meine Organisation"
      onBack={handleBackToDashboard}
    />
  );
}

// Wrapper für Organization Members Management Route - MIT NAVIGATION
function OrganizationMembersWrapper({ user }) {
  const navigate = useNavigate();
  const { organizationId } = useParams(); // organizationId aus URL lesen
  
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    } else if (section === 'org-dashboard') {
      // Verwende die organizationId aus der URL falls vorhanden
      if (organizationId) {
        navigate(`/org-dashboard/${organizationId}`);
      } else {
        navigate('/org-dashboard');
      }
    } else if (section === 'team-dashboard') {
      navigate('/my-team-dashboard');
    } else if (section === 'organization-management') {
      // Verwende die organizationId aus der URL falls vorhanden
      if (organizationId) {
        navigate(`/organization-management/${organizationId}`);
      } else {
        navigate('/organization-management');
      }
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="organization-members"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => {
          if (organizationId) {
            navigate(`/organization-management/${organizationId}`);
          } else {
            navigate('/organization-management');
          }
        }}
        onOpenMembersManagement={() => {
          if (organizationId) {
            navigate(`/organization-members/${organizationId}`);
          } else {
            navigate('/organization-members');
          }
        }}
        onViewOrganizationLeads={() => {
          if (organizationId) {
            navigate(`/organization-leads/${organizationId}`);
          } else {
            navigate('/organization-leads');
          }
        }}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <OrganizationMembersManagement 
          user={user} 
          organizationId={organizationId} // organizationId an Komponente übergeben
          onBack={handleBackToDashboard}
        />
      </div>
    </>
  );
}

// Spezielle Team Dashboard Wrapper für dynamische Team IDs - MIT NAVIGATION
function DynamicTeamDashboardWrapper({ user }) {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const teamId = window.location.pathname.split('/')[2]; // aus /team-dashboard/123
  const teamName = urlParams.get('name') || 'Team';
  const organizationId = urlParams.get('orgId'); // organizationId aus URL-Parametern extrahieren
  
  const handleBackToOrganization = () => {
    // Wenn organizationId vorhanden ist, zur org-dashboard mit organizationId navigieren
    if (organizationId) {
      navigate(`/org-dashboard/${organizationId}`);
    } else {
      // Fallback: zur Standard org-dashboard Route
      navigate('/org-dashboard');
    }
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    } else if (section === 'org-dashboard') {
      navigate('/org-dashboard');
    } else if (section === 'team-dashboard') {
      navigate('/my-team-dashboard');
    } else if (section === 'organization-management') {
      navigate('/organization-management');
    }
  };

  const selectedTeam = {
    id: teamId,
    name: teamName
  };

  return (
    <>
      <DashboardNav 
        activeSection="team-dashboard"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <TeamDashboard 
          user={user}
          selectedTeam={selectedTeam}
          onBackToOrganization={handleBackToOrganization}
        />
      </div>
    </>
  );
}

// Wrapper für CreateUser mit Navigation
function CreateUserWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    } else if (section === 'org-dashboard') {
      navigate('/org-dashboard');
    } else if (section === 'team-dashboard') {
      navigate('/my-team-dashboard');
    } else if (section === 'organization-management') {
      navigate('/organization-management');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="create-user"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <CreateUser user={user} />
      </div>
    </>
  );
}

// Wrapper für Sub-Team-Verwaltung
function GlobalSubTeamManagementWrapper({ user }) {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  
  const handleBackToOrganization = () => {
    // Organisations-ID mitschicken, falls vorhanden
    if (organizationId) {
      navigate(`/organization-management/${organizationId}`);
    } else {
      navigate('/organization-management');
    }
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="organization-management"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button 
              onClick={handleBackToOrganization}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4a5568',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ← Zurück zur Organisationsverwaltung
            </button>
          </div>
          <GlobalSubTeamManagement 
            user={user} 
            organizationId={organizationId}
          />
        </div>
      </div>
    </>
  );
}

// Wrapper für Callcenter Dashboard mit Navigation
function CallcenterDashboardWrapper({ user }) {
  const navigate = useNavigate();
  const handleLogout = () => {
    navigate('/');
  };
  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/kunden');
    } else if (section === 'leads-overview') {
      navigate('/leads');
    } else if (section === 'org-dashboard') {
      navigate('/org-dashboard');
    } else if (section === 'team-dashboard') {
      navigate('/my-team-dashboard');
    }
  };
  return (
    <>
      <DashboardNav
        activeSection="callcenter-dashboard"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <CallcenterDashboard user={user} />
      </div>
    </>
  );
}


// Wrapper für My Bewertungen
function MyBewertungenWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'my-bewertungen') {
      navigate('/my-bewertungen');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="my-bewertungen"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <MyBewertungen user={user} onBack={handleBackToDashboard} />
      </div>
    </>
  );
}

// Wrapper für Lagerverwaltung (Organisationsleiter)
function WarehouseManagementWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'warehouse') {
      navigate('/warehouse');
    } else if (section === 'orders-management') {
      navigate('/orders-management');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="warehouse"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <WarehouseManagement user={user} />
      </div>
    </>
  );
}

// Wrapper für Bestellungen (Agenturleiter)
function OrdersWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'orders') {
      navigate('/orders');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="orders"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <Orders user={user} />
      </div>
    </>
  );
}

// Wrapper für Bestellverwaltung (Organisationsleiter)
function OrderManagementWrapper({ user }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'orders-management') {
      navigate('/orders-management');
    } else if (section === 'warehouse') {
      navigate('/warehouse');
    }
  };

  return (
    <>
      <DashboardNav 
        activeSection="orders-management"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate('/organization-management')}
        onOpenMembersManagement={() => navigate('/organization-members')}
        onViewOrganizationLeads={() => navigate('/organization-leads')}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div className="page-container">
        <OrderManagement user={user} />
      </div>
    </>
  );
}

// Wrapper für AgenturLeads mit Team-ID
function AgenturLeadsTeamWrapper({ user }) {
  const { agenturId, teamId, customerId } = useParams();
  const navigate = useNavigate();

  const handleBackToTeams = () => {
    navigate(`/agenturen/${agenturId}/teams`);
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'agenturen') {
      navigate('/agenturen');
    }
  };

  return (
    <AgenturLeads 
      user={user} 
      agenturId={agenturId} 
      a_team_id={teamId}
      onBack={handleBackToTeams}
    />
  );
}

// Wrapper für Team Dashboard Route mit Agentur und Team ID
function TeamDashboardAgenturWrapper({ user }) {
  const { agenturId, teamId } = useParams();
  const navigate = useNavigate();
  
  const handleBackToTeams = () => {
    navigate(`/agenturen/${agenturId}/teams`);
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'agenturen') {
      navigate('/agenturen');
    } else if (section === 'agentur-dashboard') {
      navigate(`/agentur-dashboard/${agenturId}`);
    } else if (section === 'mitglieder') {
      navigate(`/agenturen/${agenturId}/teams/${teamId}/mitarbeiter`);
    } else if (section === 'leads') {
      navigate(`/agenturen/${agenturId}/teams/${teamId}/leads`);
    }
  };

  // Erstelle ein selectedTeam-Objekt basierend auf den URL-Parametern
  const selectedTeam = {
    id: teamId,
    agentur_id: agenturId
  };

  return (
    <>
      <DashboardNav
        activeSection="team-dashboard"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => {}}
        onOpenMembersManagement={() => navigate(`/agenturen/${agenturId}/teams/${teamId}/mitarbeiter`)}
        onViewOrganizationLeads={() => navigate(`/agenturen/${agenturId}/teams/${teamId}/leads`)}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={true}
        showOrganizationLeads={true}
      />
      <div className="page-container">
        <TeamDashboard 
          user={user}
          selectedTeam={selectedTeam}
          onBackToOrganization={handleBackToTeams}
        />
      </div>
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const [isLoading, setIsLoading] = useState(true);

  // Beim ersten Laden prüfen, ob ein Benutzer im localStorage gespeichert ist
  useEffect(() => {
    const checkStoredUser = async () => {
      try {
        // Zuerst versuchen, den User aus dem user-Eintrag zu laden
        let storedUser = localStorage.getItem('user');
        let userData = null;
        
        if (storedUser) {
          try {
            userData = JSON.parse(storedUser);
          } catch (parseError) {
            console.error('Fehler beim Parsen der user-Daten:', parseError);
            localStorage.removeItem('user');
          }
        }
        
        // Falls kein user-Eintrag vorhanden ist, versuchen wir es mit sessionData
        if (!userData) {
          const sessionData = localStorage.getItem('sessionData');
          if (sessionData) {
            try {
              const parsedSessionData = JSON.parse(sessionData);
              if (parsedSessionData && parsedSessionData.user) {
                userData = parsedSessionData.user;
                console.log('User aus sessionData wiederhergestellt:', userData.email);
                
                // User-Eintrag für Kompatibilität wiederherstellen
                localStorage.setItem('user', JSON.stringify(userData));
              }
            } catch (parseError) {
              console.error('Fehler beim Parsen der sessionData:', parseError);
              localStorage.removeItem('sessionData');
            }
          }
        }
        
        // Validierung der gespeicherten Daten
        if (userData && userData.id && userData.email) {
          // Rollenflags immer setzen, nicht nur wenn isAdmin fehlt
          if (userData.role_type) {
            const roleType = parseInt(userData.role_type, 10);
            userData.isAdmin = roleType === 1;
            userData.isTeamlead = roleType === 2;
            userData.isOrgLeader = roleType === 3;
            userData.isCallcenterLeader = roleType === 4;
            userData.isCallcenterMitarbeiter = roleType === 5;
            userData.isFachberater = roleType === 6;
            userData.isAgenturLeader = roleType === 7;
          }
          
          // Für bestehende Benutzer: Fachberater-Flag setzen falls role_type 6 ist
          if (userData.role_type === 6 && !userData.hasOwnProperty('isFachberater')) {
            userData.isFachberater = true;
          }
          
          // Prüfen ob Benutzer als Teamleiter in sub_teamleiter Tabelle steht
          try {
            const teamleiterCheck = await checkIsSubTeamleiter(userData.id);
            if (teamleiterCheck.success) {
              userData.isSubTeamleiter = teamleiterCheck.isSubTeamleiter;
            } else {
              userData.isSubTeamleiter = false;
            }
          } catch (err) {
            console.error('Fehler beim Prüfen des Teamleiter-Status:', err);
            userData.isSubTeamleiter = false;
          }
          
          setUser(userData);
          
          // Tab-Titel basierend auf der Organisation setzen
          if (userData.organization_id) {
            updateTabTitle(userData.organization_id);
          }
        } else {
          // Ungültige Daten, nur user-Eintrag bereinigen
          localStorage.removeItem('user');
          // sessionData nicht löschen, da es gültige Daten enthalten könnte
          console.warn('Ungültige user-Daten, aber sessionData bleibt erhalten');
        }
      } catch (error) {
        console.error('Fehler beim Laden der Benutzerdaten aus localStorage:', error);
        localStorage.removeItem('user');
        // sessionData nicht löschen, da es gültige Daten enthalten könnte
        console.warn('Fehler beim Laden, aber sessionData bleibt erhalten');
      } finally {
        setIsLoading(false);
      }
    };

    checkStoredUser();
  }, []);

  const handleLogin = (userData) => {
    if (!userData || !userData.id || !userData.email) {
      console.error("Ungültige Benutzerdaten erhalten:", userData);
      return;
    }
    
    try {
      // Session initialisieren
      initializeSession(userData);
      
      // Benutzerdaten im localStorage speichern
      localStorage.setItem('user', JSON.stringify(userData));
      
      setUser(userData);
      
      // Tab-Titel basierend auf der Organisation setzen
      if (userData.organization_id) {
        updateTabTitle(userData.organization_id);
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Benutzerdaten:', error);
      setUser(userData);
    }
  };

  const handleLogout = () => {
    // Session bereinigen
    cleanupSession();
    
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionData');
    sessionStorage.clear();
    window.location.href = '/';
  };

  const switchToRegister = () => {
    setAuthMode('register');
  };

  const switchToLogin = () => {
    setAuthMode('login');
  };

  // Session-Überwachung für angemeldete Benutzer
  useSessionTimeout(90, handleLogout);
  
  // Erweiterte Session-Überwachung mit User-Status-Prüfung
  useExtendedSessionMonitoring(90, (deactivatedUser) => {
    console.log('User wurde deaktiviert:', deactivatedUser);
    alert('Ihr Account wurde deaktiviert. Sie werden ausgeloggt.');
    handleLogout();
  }, (deactivatedTeam) => {
    console.log('Team wurde deaktiviert:', deactivatedTeam);
    alert(`Ihr Team "${deactivatedTeam.name}" wurde deaktiviert. Sie werden ausgeloggt.`);
    handleLogout();
  });

  // Schütze Routen, die nur für angemeldete Benutzer zugänglich sein sollen
  const ProtectedRoute = ({ children }) => {
    if (isLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '1.2rem',
          color: '#4a5568'
        }}>
          Laden...
        </div>
      );
    }

    if (!user || !user.id) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  // Rollenschutz für Admin/Org-Leader/Team-Leader Routen
  const AdminOrgLeaderRoute = ({ children }) => {
    if (!user?.isAdmin && !user?.isOrgLeader && !user?.isTeamlead) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  // Rollenschutz nur für Admin/Org-Leader Routen  
  const AdminOrgRoute = ({ children }) => {
    if (!user?.isAdmin && !user?.isOrgLeader) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  // Rollenschutz für Fachberater (sollen nicht auf manche Seiten)
  const NonFachberaterRoute = ({ children }) => {
    if (user?.isFachberater) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  // Rollenschutz für Callcenter (Leader + Mitarbeiter)
  const CallcenterRoute = ({ children }) => {
    if (!user?.isCallcenterLeader && !user?.isCallcenterMitarbeiter) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  // Rollenschutz für Team-Leader
  const TeamLeaderRoute = ({ children }) => {
    if (!user?.isTeamlead) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  // Rollenschutz für Organisationsleiter
  const OrgLeaderRoute = ({ children }) => {
    if (!user?.isOrgLeader) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  // Rollenschutz für Agenturleiter
  const AgenturLeaderRoute = ({ children }) => {
    if (!user?.isAgenturLeader) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  console.log("Das ist der User: ", user);
  console.log("Das ist die OrganisationID: ", user?.organization_id);

  // Während des initialen Ladens zeige einen Ladebildschirm
  if (isLoading) {

    let loadingText = "Wird geladen...";

    if (user?.organization_id === 4) {
      loadingText = 'BEN OS wird geladen...';
    } else if (user?.organization_id === 6) {
      loadingText = 'E+ OS wird geladen...';
    }

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.5rem',
        color: '#4a5568',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        {loadingText}
      </div>
    );
  }

  return (
    <>
      <Router>
        <div className="app">
          <Routes>
            {/* Login/Register Route */}
            <Route path="/" element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                authMode === 'login' ? (
                  <Login onLogin={handleLogin} switchToRegister={switchToRegister} />
                ) : (
                  <Register switchToLogin={switchToLogin} />
                )
              )
            } />
            
            {/* Dashboard Route */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            } />
            
            {/* === CALLCENTER ROUTEN === */}
            
            {/* Alle Kunden/Leads */}
            <Route path="/kunden" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="all" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />
            
            {/* Offene Leads */}
            <Route path="/kunden-offene-leads" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="open" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />
            
            <Route path="/kunden-ohne-opt-in" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="ohne-opt-in" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />

            {/* On Hold Leads */}
            <Route path="/kunden-on-hold" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="on-hold" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />
            
            {/* Termin Abgesagt */}
            <Route path="/kunden-termin-abgesagt" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="termin-abgesagt" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />
            
            {/* Nicht erreichte Kunden */}
            <Route path="/kunden-nicht-erreicht" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="ne-kunden" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />
            
            {/* Später anrufen */}
            <Route path="/kunden-spaeter-anrufen" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="spaeter-anrufen" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />

            {/* Test Leads */}
            <Route path="/kunden-test-leads" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="test-leads" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />

            {/* Final nicht erreicht */}
            <Route path="/kunden-final-nicht-erreicht" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="final-nicht-erreicht" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />

            {/* Final abgesagt */}
            <Route path="/kunden-final-abgesagt" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterLeadsWrapper user={user} filter="final-abgesagt" />
                </CallcenterRoute>
              </ProtectedRoute>
            } />

            {/* === NORMALE ROUTEN FÜR NICHT-CALLCENTER === */}
            
            {/* Normale Kunden Route für Nicht-Callcenter */}
            <Route path="/leads-normal" element={
              <ProtectedRoute>
                <NonFachberaterRoute>
                  <LeadsWrapper user={user} />
                </NonFachberaterRoute>
              </ProtectedRoute>
            } />
            
            {/* === DASHBOARD/MANAGEMENT ROUTEN MIT NAVIGATION === */}
            
            

            {/* My Bewertungen Route */}
            <Route path="/my-bewertungen" element={
              <ProtectedRoute>
                <MyBewertungenWrapper user={user} />
              </ProtectedRoute>
            } />

            {/* Lagerverwaltung Route - für Organisationsleiter */}
            <Route path="/warehouse" element={
              <ProtectedRoute>
                <OrgLeaderRoute>
                  <WarehouseManagementWrapper user={user} />
                </OrgLeaderRoute>
              </ProtectedRoute>
            } />

            {/* Bestellungen Route - für Agenturleiter */}
            <Route path="/orders" element={
              <ProtectedRoute>
                <AgenturLeaderRoute>
                  <OrdersWrapper user={user} />
                </AgenturLeaderRoute>
              </ProtectedRoute>
            } />

            {/* Bestellverwaltung Route - für Organisationsleiter */}
            <Route path="/orders-management" element={
              <ProtectedRoute>
                <OrgLeaderRoute>
                  <OrderManagementWrapper user={user} />
                </OrgLeaderRoute>
              </ProtectedRoute>
            } />

            {/* Agenturen Verwalten Route - für Organisationsleiter */}
            <Route path="/agenturen" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <AgenturenVerwalten user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />

            {/* Agentur Detailansicht Route */}
            <Route path="/agenturen/:id" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <AgenturManagementWrapper user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />
            
            {/* Agentur Dashboard Route */}
            <Route path="/agentur-dashboard" element={
              <ProtectedRoute>
                <AgenturDashboard user={user} selectedAgentur={user?.agentur || user?.team || null} />
              </ProtectedRoute>
            } />
            
            {/* Agentur Dashboard Route mit ID */}
            <Route path="/agentur-dashboard/:id" element={
              <ProtectedRoute>
                <AgenturDashboardRouteWrapper user={user} />
              </ProtectedRoute>
            } />

            {/* Agentur Management Route mit ID */}
            <Route path="/agenturen/:id/mitglieder" element={
              <ProtectedRoute>
                <AgenturManagementRouteWrapper user={user} />
              </ProtectedRoute>
            } />

            {/* Agentur Leads Route mit ID */}
            <Route path="/agenturen/:id/leads" element={
              <ProtectedRoute>
                <AgenturLeadsRouteWrapper user={user} />
              </ProtectedRoute>
            } />

            {/* Agentur Teams Route mit ID */}
            <Route path="/agenturen/:id/teams" element={<ProtectedRoute><AgenturTeams user={user} /></ProtectedRoute>} />
            <Route path="/agenturen/:agenturId/teams/:teamId/dashboard" element={<ProtectedRoute><TeamDashboardAgenturWrapper user={user} /></ProtectedRoute>} />
            <Route path="/agenturen/:agenturId/teams/:teamId/mitarbeiter" element={<TeamMitarbeiter user={user} />} />
            <Route path="/agenturen/:agenturId/teams/:teamId/leads" element={
              <ProtectedRoute>
                <AgenturLeadsTeamWrapper user={user} />
              </ProtectedRoute>
            } />

            {/* Agentur Team Lead Detail Route - für Detailansicht */}
            <Route path="/agenturen/:agenturId/teams/:teamId/leads/:customerId" element={
              <ProtectedRoute>
                <AgenturLeadsTeamWrapper user={user} />
              </ProtectedRoute>
            } />

            {/* Agentur Lead Detail Route - für Detailansicht */}
            <Route path="/agenturen/:id/leads/:customerId" element={
              <ProtectedRoute>
                <AgenturLeadsRouteWrapper user={user} />
              </ProtectedRoute>
            } />
            
            {/* Dynamisches Team Dashboard - für Orgleiter die Teams anschauen */}
            <Route path="/team-dashboard/:teamId" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <DynamicTeamDashboardWrapper user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />
            
            {/* Organisation Dashboard Route - für Orgleiter */}
            <Route path="/org-dashboard" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <OrganizationDashboardWrapper user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />
            
            {/* Organisation Dashboard Route mit organizationId Parameter */}
            <Route path="/org-dashboard/:organizationId" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <OrganizationDashboardWrapper user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />
            
            {/* Organisation Management Route - für Admin/Orgleiter/Teamleiter */}
            <Route path="/organization-management" element={
              <ProtectedRoute>
                <AdminOrgLeaderRoute>
                  <OrganizationManagementWrapper user={user} />
                </AdminOrgLeaderRoute>
              </ProtectedRoute>
            } />
            
            {/* Organisation Management Route mit organizationId Parameter */}
            <Route path="/organization-management/:organizationId" element={
              <ProtectedRoute>
                <AdminOrgLeaderRoute>
                  <OrganizationManagementWrapper user={user} />
                </AdminOrgLeaderRoute>
              </ProtectedRoute>
            } />
            
            {/* Team Leads Route - für Admin/Orgleiter/Teamleiter */}
            <Route path="/team-leads/:teamId" element={
              <ProtectedRoute>
                <AdminOrgLeaderRoute>
                  <TeamLeadsWrapper user={user} />
                </AdminOrgLeaderRoute>
              </ProtectedRoute>
            } />
            
            {/* Team Lead Detail Route - ohne doppelte Navigation */}
            <Route path="/team-leads/:teamId/:customerId" element={
              <ProtectedRoute>
                <AdminOrgLeaderRoute>
                  <TeamLeadsWrapper user={user} />
                </AdminOrgLeaderRoute>
              </ProtectedRoute>
            } />
            
            {/* Organisation Leads Route - für Admin/Orgleiter */}
            <Route path="/organization-leads" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <OrganizationLeadsWrapper user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />
            
            {/* Organisation Leads Route mit organizationId Parameter */}
            <Route path="/organization-leads/:organizationId" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <OrganizationLeadsWrapper user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />
            
            {/* Organisation Lead Detail Route - ohne doppelte Navigation */}
            <Route path="/organization-leads/:organizationId/:customerId" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <OrganizationLeadsWrapper user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />
            
            {/* Organisation Members Management Route - für Admin/Orgleiter */}
            <Route path="/organization-members" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <OrganizationMembersWrapper user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />
            
            {/* Organisation Members Management Route mit organizationId Parameter */}
            <Route path="/organization-members/:organizationId" element={
              <ProtectedRoute>
                <AdminOrgRoute>
                  <OrganizationMembersWrapper user={user} />
                </AdminOrgRoute>
              </ProtectedRoute>
            } />

            {/* Sub-Team-Verwaltung Route - für Admin/Orgleiter/Teamleiter */}
            <Route path="/sub-team-management" element={
              <ProtectedRoute>
                <AdminOrgLeaderRoute>
                  <GlobalSubTeamManagementWrapper user={user} />
                </AdminOrgLeaderRoute>
              </ProtectedRoute>
            } />
            
            {/* Sub-Team-Verwaltung Route mit organizationId Parameter */}
            <Route path="/sub-team-management/:organizationId" element={
              <ProtectedRoute>
                <AdminOrgLeaderRoute>
                  <GlobalSubTeamManagementWrapper user={user} />
                </AdminOrgLeaderRoute>
              </ProtectedRoute>
            } />

            {/* === BESTEHENDE ROUTEN === */}
            
            {/* Termine Route */}
            <Route path="/termine" element={
              <ProtectedRoute>
                <TermineWrapper user={user} />
              </ProtectedRoute>
            } />
            
            {/* TerminListe Route */}
            <Route path="/termin-liste" element={
              <ProtectedRoute>
                <NonFachberaterRoute>
                  <TerminListeWrapper user={user} />
                </NonFachberaterRoute>
              </ProtectedRoute>
            } />
            
            {/* Leads Route für Leads Overview */}
            <Route path="/leads" element={
              <ProtectedRoute>
                <NonFachberaterRoute>
                  <LeadsOverview user={user} />
                </NonFachberaterRoute>
              </ProtectedRoute>
            } />
            
            {/* Stellungnahmen Route */}
            <Route path="/stellungnahmen" element={
              <ProtectedRoute>
                <NonFachberaterRoute>
                  <StellungnahmenWrapper user={user} />
                </NonFachberaterRoute>
              </ProtectedRoute>
            } />
            
            {/* Create User Route - MIT NAVIGATION */}
            <Route path="/create-user" element={
              <ProtectedRoute>
                <AdminOrgLeaderRoute>
                  <CreateUserWrapper user={user} />
                </AdminOrgLeaderRoute>
              </ProtectedRoute>
            } />
            
            <Route 
              path="/customer/:customerId" 
              element={
                <ProtectedRoute>
                  <CustomerDetailPage user={user} />
                </ProtectedRoute>
              } 
            />

            {/* Lead Form Routes */}
            <Route path="/add-pv-lead" element={
              <ProtectedRoute>
                <NonFachberaterRoute>
                  <LeadForm user={user} />
                </NonFachberaterRoute>
              </ProtectedRoute>
            } />
            
            <Route path="/add-wp-lead" element={
              <ProtectedRoute>
                <NonFachberaterRoute>
                  <WpForm user={user} />
                </NonFachberaterRoute>
              </ProtectedRoute>
            } />

            <Route path="/add-pv-wp-lead" element={
              <ProtectedRoute>
                <NonFachberaterRoute>
                  <PvAndWPForm user={user} />
                </NonFachberaterRoute>
              </ProtectedRoute>
            } />

            <Route path="/add-pv-lead-to-customer" element={
              <ProtectedRoute>
                <NonFachberaterRoute>
                  <AddPvLeadToCustomer user={user} />
                </NonFachberaterRoute>
              </ProtectedRoute>
            } />

            <Route path="/add-wp-lead-to-customer" element={
              <ProtectedRoute>
                <NonFachberaterRoute>
                  <AddWpLeadToCustomer user={user} />
                </NonFachberaterRoute>
              </ProtectedRoute>
            } />

            {/* Callcenter Dashboard Route */}
            <Route path="/callcenter-dashboard" element={
              <ProtectedRoute>
                <CallcenterRoute>
                  <CallcenterDashboardWrapper user={user} />
                </CallcenterRoute>
              </ProtectedRoute>
            } />

            {/* Passwort-Reset Routes - öffentlich zugänglich (ohne Authentifizierung) */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Opt-In Bestätigung Route - öffentlich zugänglich (ohne Authentifizierung) */}
            <Route path="/optin-confirmation" element={<OptInConfirmation />} />

            {/* Fallback für unbekannte Routen */}
            <Route path="*" element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </Router>
    </>
  );
}

export default App;
